export type Lang = "de" | "en";

export type Translation = {
  nav: {
    features: string;
    pricing: string;
    how: string;
    insights: string;
    blog: string;
    startMonitoring: string;
    getStarted: string;
    bookDemo: string;
    languageLabel: string;
    de: string;
    en: string;
    login: string;
    tryFree: string;
  };
  hero: {
    titlePrefix: string;
    titleAccent: string;
    highlight: string;
    description: string;
    primaryCta: string;
    secondaryCta: string;
    bookDemo: string;
  };
  socialProof: {
    title: string;
    stats: { end: number; label: string }[];
  };
  problems: {
    heading: string;
    description: string;
    solutionHeading: string;
    solutionDescription: string;
  };
  benefits: {
    heading: string;
    items: { title: string; desc: string }[];
  };
  how: {
    heading: string;
    steps: { num: string; title: string; desc: string }[];
  };
  liveFeed: {
    heading: string;
    subheading: string;
    signalSourceLabel: string;
    tenderLabel: string;
    viewDetails: string;
    goToSlideLabel: string;
    projects: {
      title: string;
      city: string;
      summary: string;
      signalSource: string;
      tenderTimeline: string;
      date: string;
      tags: string[];
    }[];
  };
  features: {
    heading: string;
    items: { title: string; desc: string }[];
  };
  testimonials: {
    heading: string;
    entries: { quote: string; author: string; role: string; city: string }[];
  };
  pricing: {
    heading: string;
    subheading: string;
    popularLabel: string;
    billingToggle: {
      monthly: string;
      yearly: string;
      /** Badge next to "Yearly" — e.g. "Save 20%" */
      saveBadge: string;
    };
    plans: {
      name: string;
      /** Monthly-billed price (e.g. "€19"). For Free / Enterprise this is the literal label ("€0", "Custom"). */
      price: string;
      /** Period suffix for the monthly price (e.g. "/month"). Blank for Free / Enterprise. */
      period: string;
      /**
       * When the yearly toggle is active, plans with a yearly tier swap the
       * displayed price to this monthly-equivalent string (e.g. "€15") and
       * render `priceYearlyNote` underneath. Omit on Free / Enterprise.
       */
      priceYearly?: string;
      /** Sub-line shown below the yearly price (e.g. "€180 billed annually"). */
      priceYearlyNote?: string;
      /**
       * If set, rendered as an italic line above the features list
       * (e.g. "Everything in Starter, plus:"). Skipped on the base tier.
       */
      inheritedFrom?: string;
      features: string[];
      cta: string;
      highlight?: boolean;
      contact?: boolean;
    }[];
  };
  pricingTable: {
    heading: string;
    subheading: string;
    popularLabel: string;
    billingToggle: {
      monthly: string;
      yearly: string;
      saveBadge: string;
    };
    plans: {
      key: string;
      name: string;
      price: string;
      period: string;
      priceYearly?: string;
      priceYearlyNote?: string;
      cta: string;
      highlight?: boolean;
      contact?: boolean;
    }[];
    groups: {
      label: string;
      rows: {
        label: string;
        desc?: string;
        tooltip?: string;
        /** Per-plan value keyed by plan.key. true → check, false → em-dash, string → literal label. */
        values: Record<string, string | boolean>;
      }[];
    }[];
    /** Mobile-only labels for the swipe-deck nav. */
    mobile: {
      prevLabel: string;
      nextLabel: string;
      compareLabel: string;
    };
  };
  cta: {
    heading: string;
    highlight: string;
    description: string;
    cta: string;
  };
  demo: {
    badge: string;
    title: string;
    description: string;
    sectionTitle: string;
    sectionDescription: string;
    ctaLabel: string;
    bookMeetingLabel: string;
    footer: string;
  };
  bausignale: {
    titlePrefix: string;
    titleAccent: string;
    description: string;
    bullets: string[];
    ctaPrimary: string;
    ctaPrimarySublabel: string;
    ctaSecondary: string;
    ctaSecondarySublabel: string;
    trustHeading: string;
    stats: { value: string; label: string }[];
  };
  vorsprung: {
    hero: {
      h1: string;
      h1Kicker: string;
      sub: string;
      sourceLabel: string;
      ctaPrimary: string;
      ctaNote: string;
      ctaSecondary: string;
      stackNote: string;
    };
    timeline: {
      headLead: string;
      headAccent: string;
      lead?: string;
      cta: string;
      steps: { label: string; sub: string; badge?: string; arrow?: string }[];
    };
    scanner: {
      dsgvo: string;
    };
    digest: {
      heading: string;
      body: string;
      cta: string;
      cardWeek: string;
      signal: {
        category: string;
        budget: string;
        title: string;
        meta: string;
        desc: string;
        foot: string;
      };
      hint: string;
      sourceLabel: string;
    };
    monitor: {
      headingA: string;
      headingB: string;
      body: string;
      cta: string;
      liveLabel: string;
      whyLabel: string;
      note: string;
    };
    customerQuote: {
      label: string;
      headingA: string;
      headingAccent: string;
      headingB: string;
      sub: string;
      quotes: string[];
      author: string;
      prevLabel: string;
      nextLabel: string;
    };
    faq: {
      heading: string;
      readMore: string;
      items: { q: string; a: string; link?: { href: string; label?: string } }[];
    };
    demo: {
      heading: string;
      body: string;
      signatureName: string;
      signatureRole: string;
      signatureLinkLabel: string;
      cta: string;
    };
    footerCta: {
      heading: string;
      cta: string;
      ctaNote: string;
    };
    weekCards: {
      eyebrow: string;
      sub: string;
      cta: string;
      whatDecided: string;
      dossier: {
        projectLabel: string;
        decidersLabel: string;
        reveal: string;
        namePlaceholder: string;
        stages: string[];
      };
    };
    audience: {
      eyebrow: string;
      heading: string;
      sub: string;
      items: { title: string; line: string; tags: string[] }[];
    };
    coverage: {
      stats: { num: string; label: string }[];
      cities: string[];
      caption: string;
    };
    benefits: {
      heading: string;
      items: { title: string; desc: string }[];
    };
    beforeAfter: {
      heading: string;
      portalLabel: string;
      neoLabel: string;
      badge: string;
      rows: { without: string; with: string }[];
    };
  };
  /**
   * Overrides for `/vorsprung-demo` — the same landing page pointed at the
   * booking page instead of app signup. Everything else reuses `vorsprung`.
   */
  vorsprungDemo: {
    ctaPrimary: string;
    ctaPrimaryShort: string;
    ctaNote: string;
    pricingNote: string;
    /** Replaces the "open in NeoSearch" inline links, which now book a call. */
    inlineLink: string;
    heroSub: string;
    timelineHeadLead: string;
    timelineHeadAccent: string;
    timelineLead: string;
    timelineSteps: string[];
  };
  /**
   * Overrides for `/anfrage` — the same `/vorsprung` page, but every CTA
   * scrolls to the inline lead form instead of navigating to signup/booking.
   */
  vorsprungForm: {
    ctaPrimary: string;
    ctaPrimaryShort: string;
    ctaNote: string;
  };
  demoBooking: {
    title: string;
    lead: string;
    points: string[];
    scrollCta: string;
    schedulerHeading: string;
    loading: string;
    signatureName: string;
    signatureRole: string;
    signatureLinkLabel: string;
  };
  mipim: {
    badge: string;
    headline: string;
    subheadline: string;
    supportingLine: string;
    primaryCta: string;
    secondaryCta: string;
    statsItems: { value: string; desc: string }[];
    whatHeading: string;
    whatPara: string;
    whatSources: string[];
    audienceHeading: string;
    audiences: { role: string; headline: string; body: string }[];
    valueHeading: string;
    valueItems: string[];
    howHeading: string;
    howSteps: { num: string; title: string; desc: string }[];
    howReassurance: string;
    examplesHeading: string;
    examples: { title: string; body: string; tag: string }[];
    closingHeadline: string;
    closingSubline: string;
    closingCta: string;
    closingFootnote: string;
  };
  proof: {
    clientsLabel: string;
    pressLabel: string;
  };
  anfrage: {
    eyebrow: string;
    heading: string;
    sub: string;
    nameLabel: string;
    emailLabel: string;
    companyLabel: string;
    intentLabel: string;
    intentPlaceholder: string;
    regionsLabel: string;
    regionPlaceholder: string;
    wholeGermany: string;
    states: string[];
    messageLabel: string;
    messagePlaceholder: string;
    optionalTag: string;
    submitCta: string;
    submitting: string;
    requiredNote: string;
    requiredError: string;
    emailInvalidError: string;
    successTitle: string;
    successBody: string;
    successDemoPrompt: string;
    successDemoCta: string;
    errorTitle: string;
    errorBody: string;
    retryCta: string;
  };
  footer: {
    productDesc: string;
    productLine: string;
    productHeading: string;
    legalHeading: string;
    contact: string;
    dataSecurity: string;
    imprint: string;
    features: string;
    pricing: string;
    how: string;
    insights: string;
    blog: string;
    copyright: string;
    complianceNote: string;
  };
  cookieConsent: {
    bannerText: string;
    settingsLabel: string;
    learnMoreLabel: string;
    acceptAllLabel: string;
    acceptNecessaryLabel: string;
    modalTitle: string;
    necessaryTitle: string;
    necessaryDescription: string;
    analyticsTitle: string;
    analyticsDescription: string;
    marketingTitle: string;
    marketingDescription: string;
    cancelLabel: string;
    saveLabel: string;
  };
};

export const translations: Record<Lang, Translation> = {
  de: {
    nav: {
      features: "Funktionen",
      pricing: "Preise",
      how: "So funktioniert's",
      insights: "Insights",
      blog: "Blog",
      startMonitoring: "Monitoring starten",
      getStarted: "Loslegen",
      bookDemo: "Demo buchen",
      languageLabel: "Sprache wechseln",
      de: "DE",
      en: "EN",
      login: "Anmelden",
      tryFree: "14 Tage kostenlos testen",
    },
    hero: {
      titlePrefix: "In die Pole Position für",
      titleAccent: "Bauprojekte in Deutschland.",
      highlight: "",
      description:
        "NeoSearch beobachtet Ratsunterlagen, Bauleitplanung und Ausschussdokumente deutscher Kommunen – damit Sie neue Bauvorhaben erkennen, bevor sie auf klassischen Ausschreibungsportalen erscheinen.",
      primaryCta: "Monitoring starten",
      secondaryCta: "Demo ansehen",
      bookDemo: "Demo buchen",
    },
    socialProof: {
      title: "Vertrauen von Bau-Profis",
      stats: [
        { end: 247, label: "Projekte im Blick" },
        { end: 89, label: "Aktive Kunden" },
        { end: 240, label: "Abgedeckte Städte" },
      ],
    },
    problems: {
      heading: "Die Herausforderung bei Frühindikatoren im Bau",
      description:
        "Kritische Informationen sind über mehrere Quellen und Portale verstreut. <strong><u>Teams verlassen sich auf manuelles Monitoring, was zu verpassten Einblicken</u></strong> und verzögerten Entscheidungen führt.",
      solutionHeading: "Lösung",
      solutionDescription:
        "Daten aus mehreren Quellen werden automatisch gesammelt und in einer Plattform vereinheitlicht. <strong><u>Benutzer erhalten klare, strukturierte Einblicke</u></strong>, die schnellere und sicherere Entscheidungen ermöglichen.",
    },
    benefits: {
      heading: "Umfassende Plattform für Bauinformationen",
      items: [
        {
          title: "Frühe Projekt-Signale",
          desc: "Hinweise auf Bauvorhaben, bevor offizielle Ausschreibungen starten",
        },
        {
          title: "Kein manuelles Monitoring",
          desc: "Automatisches Monitoring kommunaler Quellen in ganz Deutschland",
        },
        {
          title: "Ein Dashboard",
          desc: "Zentrale Übersicht über Städte, Projekte und Entwicklungsaktivität",
        },
        {
          title: "KI-Zusammenfassungen",
          desc: "Kommunale Dokumente verständlich aufbereitet – ohne stundenlange Lektüre",
        },
      ],
    },
    how: {
      heading: "So funktioniert NeoSearch",
      steps: [
        {
          num: "01",
          title: "Kommunen überwachen",
          desc: "Wichtige Städte wählen und automatisch auf neue Signale prüfen lassen",
        },
        {
          num: "02",
          title: "Frühe Signale erkennen",
          desc: "Planungsdokumente, Bauleitpläne und Ausschussentscheidungen im Blick behalten",
        },
        {
          num: "03",
          title: "Vor Ausschreibungen handeln",
          desc: "Chancen nutzen und Kontakte aufbauen, bevor formale Ausschreibungen starten",
        },
      ],
    },
    liveFeed: {
      heading: "Aktuelle Frühindikator-Beispiele",
      subheading: "Sehen Sie Chancen, sobald sie auftauchen",
      signalSourceLabel: "Signalquelle",
      tenderLabel: "Erwarteter Ausschreibungszeitraum",
      viewDetails: "Details ansehen",
      goToSlideLabel: "Zum Slide",
      projects: [
        {
          title:
            "Machbarkeitsprüfung zur Erweiterung eines Solarparks entlang Kreuzsand",
          city: "Erfurt",
          summary:
            "Frühe Umweltprüfung für ein 100-MW-Solarfeld mit Lärmschutz- und Netzintegration.",
          signalSource: "Agenda Umwelt- und Energieausschuss",
          tenderTimeline: "Planungsauftrag erwartet Q2 2026",
          date: "18. Nov 2025",
          tags: ["Erneuerbare", "Netzintegration", "Umweltprüfung"],
        },
        {
          title:
            "Entwurf Bebauungsänderung für integrierte Kita & Familienzentrum an der Wolbecker Straße",
          city: "Münster",
          summary:
            "Konzeptnotiz zu Kapazität, Verkehrsgutachten, Spielflächen und Bodengutachten.",
          signalSource: "Stadtentwicklungsausschuss",
          tenderTimeline: "Architektur-Vorentwurf in ~8 Wochen",
          date: "20. Nov 2025",
          tags: ["Bildung", "Kita", "Öffentliche Gebäude"],
        },
        {
          title:
            "Vorbereitende Untersuchung für 60 Sozialwohnungen in Höhenhaus",
          city: "Köln",
          summary:
            "Schattenanalyse, Straßenaufteilung, Erschließung und Finanzierungsansätze.",
          signalSource: "Bezirksvertretung Köln-Mülheim",
          tenderTimeline: "Infrastruktur-RFP vsl. Ende Q3 2026",
          date: "15. Nov 2025",
          tags: ["Wohnen", "Stadterneuerung", "Mischnutzung"],
        },
        {
          title:
            "Korridorbewertung für geschützten Radweg an der Bayreuther Straße",
          city: "Nürnberg",
          summary:
            "Querschnittsvarianten, Signalsteuerung, Sicherheitsaudit und Baumerhalt.",
          signalSource: "Planungsvermerk Verkehrsausschuss",
          tenderTimeline: "Planungsbüro-Ausschreibung in 4–6 Monaten",
          date: "22. Nov 2025",
          tags: ["Mobilität", "Radverkehr", "ÖPNV"],
        },
        {
          title:
            "Vorbescheid für Logistik-Cluster nahe Rheinauhafen (Mannheim)",
          city: "Mannheim",
          summary:
            "Konzeptuelle Kubatur, Lkw-Bewegungen, Fassadenbeispiele und Hochwasserschutz.",
          signalSource: "Vorlage Wirtschaftsförderung",
          tenderTimeline: "Vergabe vor Jahresende angestrebt",
          date: "19. Nov 2025",
          tags: ["Industrie", "Logistik", "Lieferkette"],
        },
      ],
    },
    features: {
      heading: "Erweiterte Funktionen für Bau-Intelligence",
      items: [
        {
          title: "Monitoring auf Stadtebene",
          desc: "Gezielt Kommunen oder Regionen verfolgen – bundesweit in Deutschland",
        },
        {
          title: "Keyword-Abos",
          desc: "Alerts nur für Projektbegriffe, die zu Ihrem Geschäft passen",
        },
        {
          title: "KI-Zusammenfassungen",
          desc: "Strukturierte Zusammenfassungen aus öffentlich verfügbaren Dokumenten",
        },
        {
          title: "Dokumentenanalyse",
          desc: "Automatische Analyse veröffentlichter Planungsdokumente",
        },
        {
          title: "Offizielle Quellenabdeckung",
          desc: "Monitoring über offizielle kommunale Quellen hinweg",
        },
        {
          title: "Historische Aufzeichnungen",
          desc: "Zugriff auf archivierte öffentliche Daten und Trendeinblicke",
        },
      ],
    },
    testimonials: {
      heading: "Vertraut von Branchenführern",
      entries: [
        {
          quote:
            "NeoSearch hat uns ein Großprojekt drei Monate vor der Ausschreibung gezeigt. Wir konnten uns optimal positionieren.",
          author: "Klaus M.",
          role: "CEO, Bauunternehmen",
          city: "Berlin",
        },
        {
          quote:
            "Wir sparen 90 % Recherchezeit. Die KI-Zusammenfassungen sind präzise und sofort nutzbar.",
          author: "Sarah W.",
          role: "Projektleiterin",
          city: "Hamburg",
        },
        {
          quote:
            "Frühsignale verschaffen uns einen Wettbewerbsvorteil. Wir erreichen Entscheider, bevor Wettbewerber überhaupt von Projekten erfahren.“",
          author: "Thomas K.",
          role: "Business Development",
          city: "München",
        },
      ],
    },
    pricing: {
      heading: "NeoSearch Preispläne",
      subheading:
        "Kostenlos starten. Skalieren Sie, sobald Sie mehr Suchen und Monitore brauchen.",
      popularLabel: "Beliebteste",
      billingToggle: {
        monthly: "Monatlich",
        yearly: "Jährlich",
        saveBadge: "−20%",
      },
      plans: [
        {
          name: "Kostenlos",
          price: "€0",
          period: "",
          features: [
            "Für: NeoSearch entdecken",
            "Datensätze aus 240+ deutschen Städten durchsuchen",
            "Zentralisierter Überblick kommunaler Signale",
            "Kostenloses Konto, keine Kreditkarte",
          ],
          cta: "Jetzt starten",
        },
        {
          name: "Starter",
          price: "€19",
          period: "/Monat",
          priceYearly: "€15",
          priceYearlyNote: "€180 jährlich abgerechnet",
          inheritedFrom: "Alles aus Kostenlos, plus:",
          features: [
            "Für: Regionale Spezialisten, Kleinbetriebe",
            "10 KI-Suchen / Monat",
            "1 aktiver Monitor",
            "Automatische Monitor-Alerts",
          ],
          cta: "Test starten",
        },
        {
          name: "Pro",
          price: "€49",
          period: "/Monat",
          priceYearly: "€39",
          priceYearlyNote: "€468 jährlich abgerechnet",
          inheritedFrom: "Alles aus Starter, plus:",
          features: [
            "Für: Generalist-Beratungen, Mid-Market-Firmen",
            "50 KI-Suchen / Monat",
            "3 aktive Monitore",
            "CSV-Export",
          ],
          cta: "Test starten",
          highlight: true,
        },
        {
          name: "Vertical Pro",
          price: "€99",
          period: "/Monat",
          priceYearly: "€79",
          priceYearlyNote: "€948 jährlich abgerechnet",
          inheritedFrom: "Alles aus Pro, plus:",
          features: [
            "Für: Power-Nutzer, hohe Volumina",
            "200 KI-Suchen / Monat",
            "10 aktive Monitore",
            "CSV-Export",
          ],
          cta: "Test starten",
        },
        {
          name: "Enterprise",
          price: "Individuell",
          period: "",
          inheritedFrom: "Alles aus Vertical Pro, plus:",
          features: [
            "Für: Datenpartner, Reseller, Großunternehmen",
            "Maßgeschneiderte Integrationen und Daten",
            "Persönliches Onboarding und direkter Support",
            "Individueller Vertrag und SLA",
          ],
          cta: "Sales kontaktieren",
          contact: true,
        },
      ],
    },
    pricingTable: {
      heading: "Ein Tarif, der mit Ihnen wächst.",
      subheading:
        "14 Tage kostenlos testen. Skalieren Sie, sobald Sie mehr Suchen, mehr Monitore oder eine API brauchen.",
      popularLabel: "Beliebteste",
      billingToggle: {
        monthly: "Monatlich",
        yearly: "Jährlich",
        saveBadge: "−20%",
      },
      plans: [
        {
          key: "starter",
          name: "Starter",
          price: "€19",
          period: "/Monat",
          priceYearly: "€15",
          priceYearlyNote: "€180 jährlich abgerechnet",
          cta: "14 Tage kostenlos testen",
        },
        {
          key: "pro",
          name: "Pro",
          price: "€49",
          period: "/Monat",
          priceYearly: "€39",
          priceYearlyNote: "€468 jährlich abgerechnet",
          cta: "14 Tage kostenlos testen",
          highlight: true,
        },
        {
          key: "enterprise",
          name: "Enterprise",
          price: "Individuell",
          period: "",
          cta: "Sales kontaktieren",
          contact: true,
        },
      ],
      groups: [
        {
          label: "Abdeckung",
          rows: [
            {
              label: "250+ deutsche Städte",
              desc: "Ratsbeschlüsse, Bauleitplanung, Ausschussvorlagen",
              tooltip:
                "NeoSearch bündelt 250+ kommunale Ratsinformationssysteme an einem Ort — Sie müssen nicht mehr jedes Portal einzeln aufrufen oder tausende PDFs durchsuchen. Ratsbeschlüsse, Bebauungspläne und Ausschussvorlagen werden automatisch gelesen, strukturiert und zugänglich gemacht.",
              values: { starter: true, pro: true, enterprise: true },
            },
            {
              label: "Projekt-Entscheider",
              desc: "Name und Rolle der zuständigen Entscheider je Projekt",
              tooltip:
                "Zu jedem Projekt sehen Sie die zuständigen Entscheider aus den kommunalen Originaldokumenten — mit Name, Rolle und Zuständigkeit, damit Sie wissen, wen Sie ansprechen.",
              values: { starter: false, pro: "Unbegrenzt", enterprise: "Unbegrenzt" },
            },
          ],
        },
        {
          label: "Monitore & Alerts",
          rows: [
            {
              label: "Aktive Monitore",
              desc: "Region, Sektor und Schlagwort, die NeoSearch für Sie beobachtet",
              tooltip:
                "Ein Monitor beobachtet automatisch eine Region, einen Sektor oder ein Keyword-Set und sendet passende Alerts.",
              values: { starter: "1", pro: "3", enterprise: "Unbegrenzt" },
            },
            {
              label: "Automatische Alerts",
              desc: "E-Mail-Benachrichtigung, sobald ein passendes Signal erscheint",
              values: { starter: true, pro: true, enterprise: true },
            },
          ],
        },
        {
          label: "KI & Recherche",
          rows: [
            {
              label: "KI-Suchen pro Monat",
              desc: "Freitextfragen über das gesamte Archiv",
              tooltip:
                "Manuelle Recherchen über erfasste kommunale Quellen und den bestehenden NeoSearch-Datenbestand.",
              values: { starter: "10", pro: "50", enterprise: "Unbegrenzt" },
            },
          ],
        },
        {
          label: "Export & Integration",
          rows: [
            {
              label: "CSV-Export",
              desc: "Signale direkt in Excel oder Ihr CRM laden",
              tooltip:
                "CSV-Export aller erfassten Signale — direkt nutzbar für Ihr CRM, BI- oder Analytics-System.",
              values: { starter: false, pro: true, enterprise: true },
            },
            {
              label: "Webhook & API",
              desc: "Live-Feed in Ihre eigene Pipeline",
              values: { starter: false, pro: false, enterprise: true },
            },
          ],
        },
        {
          label: "Support",
          rows: [
            {
              label: "Persönliches Onboarding",
              desc: "Ein Ansprechpartner hilft beim Setup",
              values: { starter: false, pro: false, enterprise: true },
            },
            {
              label: "Individueller Vertrag & SLA",
              desc: "Maßgeschneiderte Vereinbarung mit garantierten Reaktionszeiten",
              values: { starter: false, pro: false, enterprise: true },
            },
          ],
        },
      ],
      mobile: {
        prevLabel: "Vorheriger Tarif",
        nextLabel: "Nächster Tarif",
        compareLabel: "Tarife vergleichen",
      },
    },
    cta: {
      heading: "Entdecken Sie",
      highlight: "frühe Bauchancen in Deutschland",
      description:
        "Schließen Sie sich Bauprofis an, die kommunale Signale nutzen, um früher in Projekte einzusteigen – noch bevor offizielle Ausschreibungen starten.",
      cta: "Kostenlos testen",
    },
    demo: {
      badge: "neoSearch · Demo",
      title: "Sehen Sie neoSearch in Aktion",
      description:
        "Erleben Sie, wie wir verstreute kommunale Planungsdaten in frühe, verwertbare Projektchancen für Bauunternehmen, Zulieferer und Ingenieurbüros verwandeln.",
      sectionTitle: "Bereit, neoSearch selbst auszuprobieren?",
      sectionDescription:
        "Erstellen Sie in wenigen Minuten ein kostenloses Konto und entdecken Sie heute noch Frühindikatoren aus deutschen Kommunen.",
      ctaLabel: "Jetzt kostenlos testen",
      bookMeetingLabel: "Meeting buchen und mehr erfahren",
      footer: "neoSearch ist ein Produkt der neoBIM GmbH",
    },
    bausignale: {
      titlePrefix: "Bauprojekte früher erkennen —",
      titleAccent: "bevor sie ausgeschrieben werden.",
      description:
        "NeoSearch beobachtet Ratsunterlagen, Bauleitplanung und Ausschussdokumente — damit Sie neue Bauvorhaben erkennen, oft 3–6 Monate bevor sie auf klassischen Ausschreibungsportalen erscheinen.",
      bullets: [
        "24/7 automatisches Monitoring",
        "100% öffentliche Daten, DSGVO-konform",
        "API & Datenfeed für Ihre Pipeline",
      ],
      ctaPrimary: "Kostenlos testen →",
      ctaPrimarySublabel: "Keine Kreditkarte erforderlich",
      ctaSecondary: "Demo buchen",
      ctaSecondarySublabel: "API · Datenfeed · individuelle Integration",
      trustHeading: "Vertrauen von Bau-Experten",
      stats: [
        { value: "134+", label: "Projekte\nim Blick" },
        { value: "39+", label: "Aktive\nKunden" },
        { value: "240+", label: "Städte\nabgedeckt" },
      ],
    },
    vorsprung: {
      hero: {
        h1: "Erfahren Sie zuerst, was deutsche Kommunen als Nächstes bauen.",
        h1Kicker: "Monate bevor die Bauprojekte auf Vergabeportalen erscheinen.",
        sub: "Monate bevor die Projekte auf Vergabeportalen erscheinen — aus Ratsbeschlüssen, B-Plänen und kommunalen Vorlagen.",
        sourceLabel: "Quelle ansehen",
        ctaPrimary: "Kommende Bauprojekte finden",
        ctaNote: "Kostenlos starten. Keine Kreditkarte nötig.",
        ctaSecondary: "Demo buchen",
        stackNote: "Echte Beschlüsse aus öffentlichen Ratsinformationssystemen.",
      },
      timeline: {
        headLead: "So kommen Sie früher",
        headAccent: "ins Gespräch.",
        cta: "Kommende Bauprojekte finden",
        steps: [
          {
            label: "Region wählen",
            sub: "Eine Region, mehrere Bundesländer oder ganz Deutschland.",
          },
          {
            label: "Projektarten auswählen",
            sub: "Schulen, Kitas, Sanierung, Tiefbau oder öffentliche Gebäude.",
          },
          {
            label: "Frühe Projekte sehen",
            sub: "Mit Quelle, Budget und zuständigem Fachbereich.",
          },
        ],
      },
      scanner: {
        dsgvo:
          "DSGVO-konform aus öffentlich zugänglichen kommunalen Quellen — mit Link zum Originaldokument.",
      },
      digest: {
        heading: "Das landet in Ihrem Postfach.",
        body: "Jedes Signal, das zu Ihrem Monitor passt: Vorhaben, Budget, Beschlussdatum und die Quelle — mit einem Klick in NeoSearch geöffnet.",
        cta: "Kommende Bauprojekte finden",
        cardWeek: "BEISPIEL · EIN SIGNAL AUS IHREM MONITOR",
        signal: {
          // Filled at render time from src/data/vorsprung-records.ts (DIGEST_RECORD).
          category: "",
          budget: "",
          title: "",
          meta: "",
          desc: "Beschluss zum Neubau einer dreizügigen Grundschule als Offene Ganztagsschule, Gesamtkosten rund 24,5 Mio. €. Fertigstellung zum Schuljahr 2030/31.",
          foot: "",
        },
        hint: "Weitere Projektchancen ansehen →",
        sourceLabel: "In NeoSearch öffnen →",
      },
      monitor: {
        headingA: "Sie müssen NeoSearch nicht täglich prüfen.",
        headingB: "Neue Projekte kommen zu Ihnen.",
        body: "Region, Sektor und Schlagwörter einmal einstellen. Neue passende Projekte landen automatisch per E-Mail — mit Quelle, Budget und Fachbereich.",
        cta: "Kommende Bauprojekte finden",
        liveLabel: "POSTEINGANG · NEUE SIGNALE",
        whyLabel: "Warum relevant",
        note: "Echte Ratsbeschlüsse aus öffentlichen kommunalen Quellen.",
      },
      customerQuote: {
        label: "Referenzen",
        headingA: "Was",
        headingAccent: "Nutzer",
        headingB: "über NeoSearch sagen.",
        sub: "Aus Gesprächen, in ihren eigenen Worten.",
        prevLabel: "Vorheriges Zitat",
        nextLabel: "Nächstes Zitat",
        quotes: [
          "Ich habe mich angemeldet, um früher in Projekte reinzukommen: auf der Wettbewerbsliste, im Kontakt mit dem Investor oder der Kommune, bevor der Prozess überhaupt begonnen hat.",
          "Wenn in fünf Monaten ein großartiges Projekt starten könnte, will ich das jetzt wissen — damit wir es auf die Wettbewerbsliste schaffen.",
          "Vor NeoSearch kamen frühe Projekte über Mundpropaganda zu uns.",
        ],
        author: "NeoSearch Nutzer",
      },
      faq: {
        heading: "Häufige Fragen",
        readMore: "Mehr lesen →",
        items: [
          {
            q: "Wie funktioniert NeoSearch genau?",
            a: "NeoSearch liest kontinuierlich öffentliche Portale von 250+ deutschen Kommunen — Ratsunterlagen, Bebauungspläne, Ausschussdokumente — und liefert jedes neue Bau-Signal direkt an Sie. Zwei Wege: eigener Monitor mit Alerts, oder KI-Suche über das Archiv.",
          },
          {
            q: "Wie unterscheidet sich NeoSearch von DTVP oder TED?",
            a: "Vergabeportale zeigen Ausschreibungen. Projekte, die bereits vergabebereit sind. NeoSearch zeigt den Ratsbeschluss davor: oft 2 bis 6 Monate früher. Sie wissen, was gebaut wird, bevor es jemand ausschreiben kann.",
          },
          {
            q: "Nutzen Sie CPV-Codes wie die Vergabeportale?",
            a: "Nein — bewusst nicht. CPV-Codes existieren erst, wenn eine Ausschreibung veröffentlicht ist. NeoSearch arbeitet davor: Die KI liest die kommunalen Originaldokumente und matcht Projekte auf Ihre Begriffe in normaler Sprache — zum Beispiel „Schulbau in Bayern über 5 Mio. €“. Zu jedem Treffer sehen Sie, warum er für Sie relevant ist.",
          },
          {
            q: "Was sind KI-Suchen und wofür ist das Archiv?",
            a: "KI-Suchen sind manuelle Recherchen über alle erfassten kommunalen Quellen und den bestehenden NeoSearch-Datenbestand — fragen Sie in Freitext, zum Beispiel „Welche Parkhaus-Projekte in Baden-Württemberg über 5 Mio. €?“ Das Archiv hält jedes je erfasste Signal vor, sodass Sie frühere Treffer, Projektverläufe und vergleichbare Vorhaben später wiederfinden können.",
          },
          {
            q: "Ist das vergaberechtlich unbedenklich?",
            a: "Ja. NeoSearch strukturiert ausschließlich öffentlich verfügbare Informationen aus kommunalen Ratsinformationssystemen. Jedes Signal enthält einen Link zur Originalquelle. Es verschafft keinen Zugang zu nicht-öffentlichen Unterlagen und ersetzt keine Vergabe- oder Rechtsberatung. Wie Sie mit Auftraggebern kommunizieren, sollte den internen Compliance- und Vergaberichtlinien Ihres Unternehmens entsprechen.",
            link: { href: "/data-security/" },
          },
          {
            q: "Wie zuverlässig sind Budget- und Phasenangaben?",
            a: "Budget und Phase werden aus öffentlichen Quellen extrahiert oder abgeleitet. Wenn ein Wert geschätzt oder nicht eindeutig belegt ist, wird das im Signal markiert. Jedes Signal verlinkt zur Originalquelle, damit Sie Details vor dem Anruf prüfen können. NeoSearch liefert Signale zur frühzeitigen Orientierung, keine verbindlichen Projektdaten.",
            link: { href: "/data-security/" },
          },
        ],
      },
      demo: {
        heading: "1:1 mit Marko, unserem Principal Architect.",
        body: "Sagen Sie uns, was Sie bauen, wo, und ab welcher Projektgröße. Dann schauen wir kostenlos in Ihre Region: Was wurde zuletzt beschlossen, was ist noch nicht ausgeschrieben? Wir sprechen über Ihre Ziele: welche Projekte Sie gewinnen wollen und wo NeoSearch Ihnen den Vorsprung bringt.",
        signatureName: "Marko Lazic",
        signatureRole: "Principal Architect, NeoSearch",
        signatureLinkLabel: "LinkedIn-Profil von Marko Lazic",
        cta: "Demo buchen",
      },
      footerCta: {
        heading: "Ihr nächster Auftrag entsteht gerade in einem Rathaus.",
        cta: "Kommende Bauprojekte finden",
        ctaNote: "Kostenlos starten. Keine Kreditkarte nötig.",
      },
      weekCards: {
        eyebrow: "Aktuelle Beschlüsse, deutschlandweit",
        sub: "Echte Beschlüsse aus kommunalen Originaldokumenten — mit Budget, Quelle und den zuständigen Entscheidern.",
        cta: "Weitere Projektchancen ansehen →",
        whatDecided: "Was wurde beschlossen",
        dossier: {
          projectLabel: "Projekt-Dossier",
          decidersLabel: "Personen — zuständige Entscheider",
          reveal: "Kontakt anzeigen",
          namePlaceholder: "Vorname Nachname",
          stages: ["Haushalt", "Ausschuss", "Beschluss", "Ausschreibung erwartet"],
        },
      },
      audience: {
        eyebrow: "Für wen ist NeoSearch",
        heading: "Ihr nächster Auftrag entsteht gerade in einem Rathaus.",
        sub: "Sagen Sie NeoSearch, was Sie bauen, planen oder liefern — in Ihren Worten.",
        items: [
          {
            title: "Architektur- & Ingenieurbüros",
            line: "Öffentliche Bauherren und Wettbewerbe erkennen, bevor der Prozess startet.",
            tags: ["Schulbau", "Kita", "Krankenhausbau", "Sporthalle"],
          },
          {
            title: "Fachplaner & TGA",
            line: "Projekte vor der Leistungsphase finden — solange Anforderungen noch offen sind.",
            tags: ["Sanierung", "Brandschutz", "Energetische Sanierung"],
          },
          {
            title: "Bauunternehmen & GU",
            line: "Die Pipeline füllen, bevor die Vergabe überhaupt startet.",
            tags: ["Wohnungsbau", "Gewerbebau", "Tiefbau"],
          },
          {
            title: "Projektentwickler & Investoren",
            line: "Flächen und Quartiere sehen, bevor der Markt sie kennt.",
            tags: ["Quartiersentwicklung", "B-Plan", "Flächenentwicklung", "Altlasten"],
          },
          {
            title: "Hersteller & Zulieferer",
            line: "Objekte finden, die Ihr Produkt brauchen — vor der Ausschreibung.",
            tags: ["Parkhaus", "Flachdach", "PV & Energie", "Fassade"],
          },
          {
            title: "Berater & Kommunal-Strategen",
            line: "Kommunale Budgets und Strategien systematisch im Blick behalten.",
            tags: ["Haushalt", "Digitalisierung", "Freiraumentwicklung"],
          },
        ],
      },
      coverage: {
        stats: [
          { num: "250+", label: "Kommunen" },
          { num: "16", label: "Bundesländer" },
          { num: "1", label: "Postfach" },
        ],
        cities: [
          "Berlin",
          "München",
          "Hamburg",
          "Köln",
          "Frankfurt",
          "Leipzig",
          "Nürnberg",
          "Bremen",
          "Dresden",
          "Hannover",
        ],
        caption: "Eine Quelle für jede kommunale Bauentscheidung in Deutschland.",
      },
      benefits: {
        heading: "Alles, was Sie brauchen, um früher zu handeln.",
        items: [
          {
            title: "Frühe Projekt-Signale",
            desc: "Hinweise auf Bauvorhaben, bevor offizielle Ausschreibungen starten.",
          },
          {
            title: "Kein manuelles Monitoring",
            desc: "Automatische Überwachung kommunaler Quellen in ganz Deutschland.",
          },
          {
            title: "Ein Dashboard",
            desc: "Zentrale Übersicht über Städte, Projekte und Entwicklungen.",
          },
          {
            title: "KI-Zusammenfassungen",
            desc: "Kommunale Dokumente verständlich aufbereitet — ohne stundenlange Lektüre.",
          },
        ],
      },
      beforeAfter: {
        heading: "Früher als jedes Vergabeportal.",
        portalLabel: "Standard-Vergabeportale",
        neoLabel: "NeoSearch · kommunale Entscheidungs-Intelligenz",
        badge: "2–6 Monate früher",
        rows: [
          {
            without: "Zeigen das Projekt erst mit der Ausschreibung.",
            with: "Zeigen den Ratsbeschluss, sobald er gefasst ist.",
          },
          {
            without: "Spezifikation und Rahmen stehen bereits fest.",
            with: "Mit Budget, Quelle und zuständigem Fachbereich.",
          },
          {
            without: "Sie starten im offenen Wettbewerb — wie alle anderen.",
            with: "Sie prägen die Anforderungen, bevor ausgeschrieben wird.",
          },
        ],
      },
    },
    vorsprungDemo: {
      ctaPrimary: "Demo buchen",
      ctaPrimaryShort: "Demo",
      ctaNote: "30 Minuten, unverbindlich — mit echten Projekten aus Ihrer Region.",
      pricingNote: "Wir zeigen Ihnen in der Demo, welcher Plan zu Ihnen passt.",
      inlineLink: "In der Demo live ansehen →",
      heroSub:
        "NeoSearch überwacht kommunale Quellen und meldet passende Bauprojekte für Ihre Region – Monate bevor sie auf Vergabeportalen erscheinen.",
      timelineHeadLead: "Bauprojekte früher",
      timelineHeadAccent: "entdecken.",
      timelineLead:
        "In 30 Minuten prüfen wir live, welche Projekte in Ihrer Region bereits beschlossen oder geplant sind — bevor sie auf Vergabeportalen erscheinen.",
      timelineSteps: [
        "Projektarten und Suchbegriffe festlegen",
        "Treffer mit Quelle, Budget und Kontext sehen",
        "Automatische Suche einrichten",
      ],
    },
    vorsprungForm: {
      ctaPrimary: "Kurzanfrage stellen",
      ctaPrimaryShort: "Anfrage",
      ctaNote: "Kein Termin nötig — wir melden uns mit passenden Signalen.",
    },
    demoBooking: {
      title: "Ihre Wettbewerber starten mit der Ausschreibung. Sie starten jetzt.",
      lead: "Ich zeige Ihnen live, was in Ihrer Region schon beschlossen ist — mit Budget und den Entscheidern dahinter.",
      points: [
        "Sie sagen mir, was Sie bauen, planen oder liefern",
        "Ich zeige Ihnen echte Projekte aus Ihrer Region — mit Budget, Quelle und Entscheidern",
        "Sie sehen, wo Sie früh genug dran sind",
      ],
      scrollCta: "Termin auswählen",
      schedulerHeading: "Verfügbare Termine",
      loading: "Verfügbare Termine werden geladen…",
      signatureName: "Marko Lazic",
      signatureRole: "Principal Architect, NeoSearch",
      signatureLinkLabel: "LinkedIn-Profil von Marko Lazic",
    },
    mipim: {
      badge: "NeoSearch · Frühe Bau-Signale",
      headline: "Werden Sie zur Pole Position für Bauprojekte in Deutschland.",
      subheadline:
        "NeoSearch erkennt frühe Signale aus deutschen Kommunen, damit Sie Bauchancen identifizieren, bevor Ihre Wettbewerber sie sehen.",
      supportingLine:
        "Für Investoren, Projektentwickler, Zulieferer und Städte, die Projekte in Deutschland früher sehen möchten.",
      primaryCta: "Jetzt kostenlos testen",
      secondaryCta: "Meeting buchen und mehr erfahren",
      statsItems: [
        {
          value: "Wochen früher",
          desc: "Projekte sehen, bevor sie auf Ausschreibungsplattformen erscheinen",
        },
        {
          value: "Ganz Deutschland",
          desc: "Kommunale Signale aus allen Bundesländern",
        },
        {
          value: "4 Zielgruppen",
          desc: "Eine Plattform für die gesamte Wertschöpfungskette im Bau",
        },
      ],
      whatHeading: "Was NeoSearch macht",
      whatPara:
        "NeoSearch beobachtet Ratsunterlagen, Ausschussagenden und Bauleitpläne in ganz Deutschland. Sobald ein neues Bauvorhaben entsteht, macht NeoSearch es sichtbar – lange bevor daraus eine öffentliche Ausschreibung wird.",
      whatSources: [
        "Tagesordnungen und Protokolle von Rats- und Ausschusssitzungen",
        "Bauleitplanung, Flächennutzungs- und Bebauungsplanänderungen",
        "Beschlüsse von Bau- und Planungsausschüssen",
        "Bekanntmachungen zu Entwicklungs- und Infrastrukturprojekten",
      ],
      audienceHeading: "Für wen NeoSearch gebaut ist",
      audiences: [
        {
          role: "Investoren",
          headline: "Märkte qualifizieren, bevor Kapital fließt.",
          body: "Erhalten Sie Einblick in Projektpipelines und Entwicklungssignale in deutschen Städten – bevor Objekte offen am Markt erscheinen.",
        },
        {
          role: "Projektentwickler & Eigentümer",
          headline:
            "Auf kommunale Entscheidungen reagieren, bevor andere es tun.",
          body: "Verfolgen Sie Bebauungsplanänderungen, Genehmigungen und benachbarte Projekte, die Ihre Vorhaben beeinflussen.",
        },
        {
          role: "Zulieferer & Bauunternehmen",
          headline: "Beziehungen gewinnen, bevor Ausschreibungen starten.",
          body: "Identifizieren Sie Projekte bereits in der Planungsphase und erreichen Sie Entscheider, bevor formale RFPs veröffentlicht werden.",
        },
        {
          role: "Städte & Regionen",
          headline: "Verstehen, was andere Kommunen bauen.",
          body: "Vergleichen Sie Entwicklungsaktivitäten, verfolgen Sie Infrastrukturtrends und treffen Sie investitionsrelevante Entscheidungen datenbasiert.",
        },
      ],
      valueHeading: "Ihr Vorteil mit NeoSearch",
      valueItems: [
        "In die Pole Position für neue Bauprojekte kommen",
        "Chancen früher erkennen als Wettbewerber",
        "Kommunale Entwicklung in ganz Deutschland im Blick behalten",
        "Vertrieb auf Projekte fokussieren, bevor Ausschreibungen veröffentlicht werden",
      ],
      howHeading: "So funktioniert NeoSearch für Sie",
      howSteps: [
        {
          num: "01",
          title: "Kommunen überwachen",
          desc: "NeoSearch scannt kontinuierlich öffentliche Daten aus deutschen Kommunen.",
        },
        {
          num: "02",
          title: "Frühe Signale erkennen",
          desc: "Planungsdokumente, Bauleitpläne und Ausschussentscheidungen zeigen kommende Projekte.",
        },
        {
          num: "03",
          title: "In die Pole Position kommen",
          desc: "Früher als andere handeln und Beziehungen aufbauen, bevor Projekte in die Ausschreibungsphase gehen.",
        },
      ],
      howReassurance:
        "NeoSearch basiert ausschließlich auf transparenten, öffentlich zugänglichen Informationen aus offiziellen kommunalen Quellen.",
      examplesHeading: "Beispiele für echte Signale",
      examples: [
        {
          title: "Logistik-Cluster nahe einem bedeutenden Hafen",
          body: "Vorbescheidsanfrage aus Unterlagen der Wirtschaftsförderung – mit geplanter Vergabe noch vor Jahresende.",
          tag: "Industrie",
        },
        {
          title: "60 Sozialwohnungen in einer Bestandslage",
          body: "Vorbereitende Untersuchung aus einer Bezirksvertretungssitzung – Infrastruktur-RFP voraussichtlich im dritten Quartal.",
          tag: "Wohnen",
        },
        {
          title: "Integrierte Kita und Familienzentrum",
          body: "Entwurf einer Bebauungsplanänderung aus dem Stadtentwicklungsausschuss – Architektenbriefing in den nächsten Wochen erwartet.",
          tag: "Bildung",
        },
      ],
      closingHeadline:
        "NeoSearch bringt Sie in die Pole Position für Bauchancen in Deutschland.",
      closingSubline: "Starten Sie kostenlos. Keine Kreditkarte erforderlich.",
      closingCta: "NeoSearch öffnen →",
      closingFootnote: "neoSearch ist ein Produkt der neoBIM GmbH",
    },
    footer: {
      productDesc: "Frühe Bau-Signale für Deutschland",
      productLine: "NeoSearch ist ein Produkt von",
      productHeading: "Produkt",
      legalHeading: "Rechtliches",
      contact: "Kontakt",
      dataSecurity: "Datenschutz & Sicherheit",
      imprint: "Impressum",
      features: "Funktionen",
      pricing: "Preise",
      how: "So funktioniert's",
      insights: "Insights",
      blog: "Blog",
      copyright: "© 2026 NeoSearch. Alle Rechte vorbehalten.",
      complianceNote:
        "NeoSearch verarbeitet ausschließlich öffentlich zugängliche Daten und vom Nutzer bereitgestellte Informationen gemäß den EU- und deutschen Datenschutzbestimmungen.",
    },
    cookieConsent: {
      bannerText: "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern.",
      settingsLabel: "Cookie-Einstellungen",
      learnMoreLabel: "Mehr erfahren",
      acceptAllLabel: "Alle akzeptieren",
      acceptNecessaryLabel: "Nur notwendige",
      modalTitle: "Cookie-Einstellungen",
      necessaryTitle: "Notwendige Cookies",
      necessaryDescription:
        "Erforderlich für die grundlegende Funktion der Website und können nicht deaktiviert werden.",
      analyticsTitle: "Analyse-Cookies",
      analyticsDescription:
        "Helfen uns zu verstehen, wie Besucher unsere Website nutzen – vollständig anonymisiert.",
      marketingTitle: "Marketing-Cookies",
      marketingDescription:
        "Werden verwendet, um Besucher über Websites hinweg für Marketingzwecke zu verfolgen.",
      cancelLabel: "Abbrechen",
      saveLabel: "Einstellungen speichern",
    },
    proof: {
      clientsLabel: "Führende Unternehmen vertrauen uns",
      pressLabel: "Bekannt aus",
    },
    anfrage: {
      eyebrow: "Kurzanfrage",
      heading: "Erzählen Sie uns, was Sie suchen.",
      sub: "Kein Termin nötig — ein kurzes Formular, wir melden uns mit den passenden Signalen.",
      nameLabel: "Name",
      emailLabel: "Geschäftliche E-Mail",
      companyLabel: "Unternehmen",
      intentLabel: "Was bauen, planen oder liefern Sie?",
      intentPlaceholder: "z. B. „Schulbau, Süddeutschland“ oder „TU/GÜ-Ausschreibungen bundesweit“",
      regionsLabel: "In welcher Region suchen Sie Projekte?",
      regionPlaceholder: "Region auswählen",
      wholeGermany: "Ganz Deutschland",
      states: [
        "Baden-Württemberg",
        "Bayern",
        "Berlin",
        "Brandenburg",
        "Bremen",
        "Hamburg",
        "Hessen",
        "Mecklenburg-Vorpommern",
        "Niedersachsen",
        "Nordrhein-Westfalen",
        "Rheinland-Pfalz",
        "Saarland",
        "Sachsen",
        "Sachsen-Anhalt",
        "Schleswig-Holstein",
        "Thüringen",
      ],
      messageLabel: "Sonstiges",
      messagePlaceholder: "Optional — alles, was uns sonst noch helfen würde.",
      optionalTag: "optional",
      submitCta: "Anfrage senden",
      submitting: "Wird gesendet …",
      requiredNote: "* Pflichtfeld",
      requiredError: "Erforderlich",
      emailInvalidError: "Ungültige E-Mail-Adresse",
      successTitle: "Danke, das kam an.",
      successBody: "Wir melden uns in Kürze persönlich bei Ihnen.",
      successDemoPrompt: "Möchten Sie nicht warten?",
      successDemoCta: "Jetzt eine Demo buchen",
      errorTitle: "Das hat leider nicht geklappt.",
      errorBody:
        "Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.",
      retryCta: "Erneut versuchen",
    },
  },
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      how: "How it Works",
      insights: "Insights",
      blog: "Blog",
      startMonitoring: "Start Monitoring",
      getStarted: "Get Started",
      bookDemo: "Book Demo",
      languageLabel: "Switch language",
      de: "DE",
      en: "EN",
      login: "Log in",
      tryFree: "Try free for 14 days",
    },
    hero: {
      titlePrefix: "Get into pole position for",
      titleAccent: "construction projects in Germany.",
      highlight: "",
      description:
        "NeoSearch monitors municipal planning records, council agendas, and zoning changes across Germany so you discover new projects before they appear on public tender platforms.",
      primaryCta: "Start Monitoring",
      secondaryCta: "See Demo",
      bookDemo: "Book Demo",
    },
    socialProof: {
      title: "Trusted by Construction Leaders",
      stats: [
        { end: 247, label: "Projects Tracked" },
        { end: 89, label: "Active Clients" },
        { end: 240, label: "Cities Covered" },
      ],
    },
    problems: {
      heading: "The Challenge of Early Construction Intelligence",
      description:
        "Critical information is scattered across multiple sources and portals. <strong><u>Teams rely on manual monitoring, leading to missed insights</u></strong> and delayed decisions.",
      solutionHeading: "Solution",
      solutionDescription:
        "Data from multiple sources is automatically collected and unified in one platform. <strong><u>Users receive clear, structured insights</u></strong> that enable faster and more confident decisions.",
    },
    benefits: {
      heading: "Comprehensive Construction Intelligence Platform",
      items: [
        {
          title: "Early Project Signals",
          desc: "See construction projects emerging in municipal data before formal tenders",
        },
        {
          title: "Zero Manual Monitoring",
          desc: "Automated tracking across German municipalities instead of manual checks",
        },
        {
          title: "One Dashboard",
          desc: "A single view of cities, projects, and development activity",
        },
        {
          title: "AI Enriched Summaries",
          desc: "Instant insights from long municipal documents without reading everything",
        },
      ],
    },
    how: {
      heading: "How NeoSearch Works",
      steps: [
        {
          num: "01",
          title: "Monitor municipalities",
          desc: "Choose the German cities you care about and let NeoSearch watch their public data",
        },
        {
          num: "02",
          title: "Detect early signals",
          desc: "Pick project-relevant keywords and see planning records and committee decisions as they appear",
        },
        {
          num: "03",
          title: "Act before tenders go live",
          desc: "Use these early signals to reach out and position yourself before formal tenders are published",
        },
      ],
    },
    liveFeed: {
      heading: "Early Intelligence Examples",
      subheading: "See new opportunities as they emerge",
      signalSourceLabel: "Signal Source",
      tenderLabel: "Expected Tender Timeline",
      viewDetails: "View Details",
      goToSlideLabel: "Go to slide",
      projects: [
        {
          title: "Feasibility Review for Solar Park Expansion along Kreuzsand",
          city: "Erfurt",
          summary:
            "Early environmental screening for a 100 MW solar field with noise impact and grid integration notes.",
          signalSource: "Umwelt- and Energieausschuss agenda",
          tenderTimeline: "Planning tender expected Q2 2026",
          date: "Nov 18, 2025",
          tags: [
            "Renewable Energy",
            "Grid Integration",
            "Environmental Review",
          ],
        },
        {
          title:
            "Draft Zoning Change for Integrated Kindergarten and Family Center on Wolbecker Strasse",
          city: "Münster",
          summary:
            "Early concept note covering capacity, traffic impact studies, playground layout, and soil assessment notes.",
          signalSource: "Stadtentwicklung committee",
          tenderTimeline:
            "Architectural pre-design brief expected within 8 weeks",
          date: "Nov 20, 2025",
          tags: ["Education", "Early Childhood", "Public Building"],
        },
        {
          title:
            "Preliminary Land Use Study for 60-Unit Affordable Housing Redevelopment in Höhenhaus",
          city: "Köln",
          summary:
            "Includes shadow analysis, street alignment options, road access diagrams, and a draft financing structure discussion.",
          signalSource: "Bezirksvertretung meeting documents",
          tenderTimeline: "Infrastructure works RFP expected late Q3 2026",
          date: "Nov 15, 2025",
          tags: ["Housing", "Urban Regeneration", "Mixed Use"],
        },
        {
          title:
            "Early Stage Corridor Assessment for Protected Cycling Path on Bayreuther Strasse",
          city: "Nürnberg",
          summary:
            "Covers cross-section alternatives, signalisation changes, safety audit, and tree preservation considerations.",
          signalSource: "Verkehrsausschuss planning memo",
          tenderTimeline: "Design engineering tender expected in 4-6 months",
          date: "Nov 22, 2025",
          tags: ["Transport", "Mobility", "Cycling"],
        },
        {
          title:
            "Pre-Application Consultation for Logistics Warehouse Cluster near Rheinau Harbour",
          city: "Mannheim",
          summary:
            "Conceptual massing, truck movement simulation, elevation samples, and floodplain mitigation notes.",
          signalSource: "Economic development council briefing",
          tenderTimeline: "Before year end",
          date: "Nov 19, 2025",
          tags: ["Industrial", "Logistics", "Supply Chain"],
        },
      ],
    },
    features: {
      heading: "Advanced Features for Construction Intelligence",
      items: [
        {
          title: "City Level Monitoring",
          desc: "Track specific municipalities or entire regions",
        },
        {
          title: "Keyword Subscriptions",
          desc: "Get alerts only for projects matching your criteria",
        },
        {
          title: "AI Summaries",
          desc: "Structured summaries from publicly available documents",
        },
        {
          title: "Document Analysis",
          desc: "Automated analysis of published planning documents",
        },
        {
          title: "Official Source Coverage",
          desc: "Monitoring across official municipal sources",
        },
        {
          title: "Historical Records",
          desc: "Access archived public data and trend insights",
        },
      ],
    },
    testimonials: {
      heading: "Trusted by industry leaders",
      entries: [
        {
          quote:
            "NeoSearch helped us discover a major infrastructure project three months before the official tender. We won the contract.",
          author: "Klaus M.",
          role: "CEO, Construction Firm",
          city: "Berlin",
        },
        {
          quote:
            "We reduced our research time by 90%. The AI summaries are incredibly accurate and save us hours every week.",
          author: "Sarah W.",
          role: "Project Manager",
          city: "Hamburg",
        },
        {
          quote:
            "The early alerts give us a competitive edge. We now engage with clients before our competitors even know projects exist.",
          author: "Thomas K.",
          role: "Business Development",
          city: "Munich",
        },
      ],
    },
    pricing: {
      heading: "NeoSearch Pricing Plans",
      subheading:
        "Start free. Scale as you need more searches and more monitors.",
      popularLabel: "Most popular",
      billingToggle: {
        monthly: "Monthly",
        yearly: "Yearly",
        saveBadge: "−20%",
      },
      plans: [
        {
          name: "Free",
          price: "€0",
          period: "",
          features: [
            "Best for: exploring NeoSearch",
            "Browse records from 240+ German cities",
            "Centralized view of municipal signals",
            "Free account, no credit card",
          ],
          cta: "Get Started",
        },
        {
          name: "Starter",
          price: "€19",
          period: "/month",
          priceYearly: "€15",
          priceYearlyNote: "€180 billed annually",
          inheritedFrom: "Everything in Free, plus:",
          features: [
            "Best for: regional specialists, small operators",
            "10 AI searches / month",
            "1 active monitor",
            "Automated monitor alerts",
          ],
          cta: "Start Trial",
        },
        {
          name: "Pro",
          price: "€49",
          period: "/month",
          priceYearly: "€39",
          priceYearlyNote: "€468 billed annually",
          inheritedFrom: "Everything in Starter, plus:",
          features: [
            "Best for: generalist consultancies, mid-market firms",
            "50 AI searches / month",
            "3 active monitors",
            "CSV Export",
          ],
          cta: "Start Trial",
          highlight: true,
        },
        {
          name: "Vertical Pro",
          price: "€99",
          period: "/month",
          priceYearly: "€79",
          priceYearlyNote: "€948 billed annually",
          inheritedFrom: "Everything in Pro, plus:",
          features: [
            "Best for: power users, high volume",
            "200 AI searches / month",
            "10 active monitors",
            "CSV Export",
          ],
          cta: "Start Trial",
        },
        {
          name: "Enterprise",
          price: "Custom",
          period: "",
          inheritedFrom: "Everything in Vertical Pro, plus:",
          features: [
            "Best for: data partners, resellers, large enterprise",
            "Tailored integrations and data delivery",
            "Dedicated onboarding and direct support",
            "Custom contract and SLA",
          ],
          cta: "Contact Sales",
          contact: true,
        },
      ],
    },
    pricingTable: {
      heading: "One plan that grows with you.",
      subheading:
        "Try free for 14 days. Scale up when you need more searches, more monitors, or an API.",
      popularLabel: "Most popular",
      billingToggle: {
        monthly: "Monthly",
        yearly: "Yearly",
        saveBadge: "−20%",
      },
      plans: [
        {
          key: "starter",
          name: "Starter",
          price: "€19",
          period: "/month",
          priceYearly: "€15",
          priceYearlyNote: "€180 billed annually",
          cta: "Try free for 14 days",
        },
        {
          key: "pro",
          name: "Pro",
          price: "€49",
          period: "/month",
          priceYearly: "€39",
          priceYearlyNote: "€468 billed annually",
          cta: "Try free for 14 days",
          highlight: true,
        },
        {
          key: "enterprise",
          name: "Enterprise",
          price: "Custom",
          period: "",
          cta: "Contact sales",
          contact: true,
        },
      ],
      groups: [
        {
          label: "Coverage",
          rows: [
            {
              label: "250+ German cities",
              desc: "Council decisions, zoning plans, committee papers",
              tooltip:
                "NeoSearch pulls 250+ municipal council information systems into one place — no more visiting each portal or trawling through thousands of PDFs. Council decisions, zoning plans and committee papers are read, structured and made searchable automatically.",
              values: { starter: true, pro: true, enterprise: true },
            },
            {
              label: "Project decision-makers",
              desc: "Name and role of the responsible decision-makers per project",
              tooltip:
                "For every project you see the responsible decision-makers from the original municipal documents — name, role and remit, so you know who to approach.",
              values: { starter: false, pro: "Unlimited", enterprise: "Unlimited" },
            },
          ],
        },
        {
          label: "Monitors & alerts",
          rows: [
            {
              label: "Active monitors",
              desc: "Region, sector and keyword that NeoSearch watches for you",
              tooltip:
                "A monitor automatically tracks a region, sector or keyword set and sends matching alerts.",
              values: { starter: "1", pro: "3", enterprise: "Unlimited" },
            },
            {
              label: "Automatic alerts",
              desc: "Email notification the moment a matching signal appears",
              values: { starter: true, pro: true, enterprise: true },
            },
          ],
        },
        {
          label: "AI & search",
          rows: [
            {
              label: "AI searches per month",
              desc: "Free-text questions across the full archive",
              tooltip:
                "Manual research across captured municipal sources and the existing NeoSearch data set.",
              values: { starter: "10", pro: "50", enterprise: "Unlimited" },
            },
          ],
        },
        {
          label: "Export & integration",
          rows: [
            {
              label: "CSV export",
              desc: "Send signals straight to Excel or your CRM",
              tooltip:
                "CSV export of every captured signal — ready to feed into your CRM, BI or analytics stack.",
              values: { starter: false, pro: true, enterprise: true },
            },
            {
              label: "Webhook & API",
              desc: "Live feed into your own pipeline",
              values: { starter: false, pro: false, enterprise: true },
            },
          ],
        },
        {
          label: "Support",
          rows: [
            {
              label: "Dedicated onboarding",
              desc: "A real person helps you set up",
              values: { starter: false, pro: false, enterprise: true },
            },
            {
              label: "Custom contract & SLA",
              desc: "Tailored agreement with guaranteed response times",
              values: { starter: false, pro: false, enterprise: true },
            },
          ],
        },
      ],
      mobile: {
        prevLabel: "Previous plan",
        nextLabel: "Next plan",
        compareLabel: "Compare plans",
      },
    },
    cta: {
      heading: "Start Discovering",
      highlight: "Early Construction Opportunities in Germany",
      description:
        "Join construction professionals using NeoSearch to be in pole position for German municipal projects before they become public tenders.",
      cta: "Start Free Trial",
    },
    demo: {
      badge: "neoSearch · Demo",
      title: "See neoSearch in action",
      description:
        "Watch how we turn scattered public planning data into early, actionable project opportunities for construction companies, suppliers, and engineering firms.",
      sectionTitle: "Ready to try neoSearch yourself?",
      sectionDescription:
        "Create a free account in minutes and start discovering early project signals from German municipalities today.",
      ctaLabel: "Try Now for Free",
      bookMeetingLabel: "Book a Meeting to Find Out More",
      footer: "neoSearch is a product of neoBIM GmbH",
    },
    bausignale: {
      titlePrefix: "Spot construction projects earlier —",
      titleAccent: "before they go to tender.",
      description:
        "NeoSearch monitors municipal planning records, council agendas, and zoning changes across Germany — so you discover new projects, often 3–6 months before they appear on public tender platforms.",
      bullets: [
        "24/7 automated monitoring",
        "100% public data, GDPR-compliant",
        "API & data feed for your pipeline",
      ],
      ctaPrimary: "Try for free →",
      ctaPrimarySublabel: "No credit card required",
      ctaSecondary: "Book a demo",
      ctaSecondarySublabel: "API · Data feed · custom integration",
      trustHeading: "Trusted by construction experts",
      stats: [
        { value: "134+", label: "Projects\ntracked" },
        { value: "39+", label: "Active\nclients" },
        { value: "240+", label: "Cities\ncovered" },
      ],
    },
    vorsprung: {
      hero: {
        h1: "Be the first to know what German municipalities build next.",
        h1Kicker: "Months before the projects appear on tender portals.",
        sub: "Months before the projects appear on tender portals — from council decisions, zoning plans and municipal documents.",
        sourceLabel: "View source",
        ctaPrimary: "Find upcoming construction projects",
        ctaNote: "Start free. No credit card required.",
        ctaSecondary: "Book a demo",
        stackNote: "Real council decisions from public municipal records.",
      },
      timeline: {
        headLead: "How you get into the conversation",
        headAccent: "earlier.",
        cta: "Find upcoming construction projects",
        steps: [
          {
            label: "Choose your region",
            sub: "One region, several federal states or all of Germany.",
          },
          {
            label: "Select project types",
            sub: "Schools, daycare centres, refurbishment, civil engineering or public buildings.",
          },
          {
            label: "See early projects",
            sub: "With source, budget and responsible department.",
          },
        ],
      },
      scanner: {
        dsgvo:
          "GDPR-compliant, from publicly accessible municipal sources — with a link to the original document.",
      },
      digest: {
        heading: "This is what lands in your inbox.",
        body: "Every signal that matches your monitor: the plan, budget, decision date and the source — opened in NeoSearch with one click.",
        cta: "Find upcoming construction projects",
        cardWeek: "SAMPLE · ONE SIGNAL FROM YOUR MONITOR",
        signal: {
          category: "",
          budget: "",
          title: "",
          meta: "",
          desc: "Resolution to build a new three-form all-day primary school, total cost around €24.5M. Completion targeted for the 2030/31 school year.",
          foot: "",
        },
        hint: "See more project opportunities →",
        sourceLabel: "Open in NeoSearch →",
      },
      monitor: {
        headingA: "You do not need to check NeoSearch every day.",
        headingB: "New projects come to you.",
        body: "Set your region, sector and keywords once. New matching projects arrive automatically by email — with source, budget and responsible department.",
        cta: "Find upcoming construction projects",
        liveLabel: "INBOX · NEW SIGNALS",
        whyLabel: "Why relevant",
        note: "Real council decisions from public municipal sources.",
      },
      customerQuote: {
        label: "Testimonials",
        headingA: "What",
        headingAccent: "users",
        headingB: "say about NeoSearch.",
        sub: "From conversations, in their own words.",
        prevLabel: "Previous quote",
        nextLabel: "Next quote",
        quotes: [
          "I signed up to get into projects earlier: on the competition list, connected to the investor or the municipality, before the process has even begun.",
          "If a great project could start in five months, I want to know now, so we can get on the list for the competition.",
          "Before NeoSearch, early projects reached us by word of mouth.",
        ],
        author: "NeoSearch user",
      },
      faq: {
        heading: "Frequently asked questions",
        readMore: "Read more →",
        items: [
          {
            q: "How does NeoSearch actually work?",
            a: "NeoSearch continuously reads the public portals of 250+ German municipalities — council papers, zoning plans, committee documents — and delivers every new construction signal directly to you. Two ways: your own monitor with alerts, or AI search across the archive.",
          },
          {
            q: "How is NeoSearch different from DTVP or TED?",
            a: "Tender portals show tenders. Projects that are already ready to be awarded. NeoSearch shows the council decision before that: often 2 to 6 months earlier. You know what is being built before anyone can put it out to tender.",
          },
          {
            q: "Do you use CPV codes like the tender portals?",
            a: "No — deliberately not. CPV codes only exist once a tender is published. NeoSearch works before that: the AI reads the original municipal documents and matches projects to your own words — for example, \"school construction in Bavaria above €5M\". Every match shows you why it is relevant to you.",
          },
          {
            q: "What are AI searches and what is the archive for?",
            a: "AI searches are manual, free-text queries across every captured municipal source and the existing NeoSearch data set — for example: \"Which car-park projects in Baden-Württemberg above €5M?\" The archive keeps every signal NeoSearch has ever captured, so you can revisit earlier hits, follow project trajectories, and compare similar past initiatives.",
          },
          {
            q: "Is this compliant with public procurement law?",
            a: "Yes. NeoSearch only structures publicly available information from municipal council systems. Every signal links to the original source. It does not grant access to non-public documents and does not replace procurement or legal advice. How you communicate with awarding bodies should follow your company's internal compliance and procurement policies.",
            link: { href: "/data-security/" },
          },
          {
            q: "How reliable are budget and phase indications?",
            a: "Budget and phase are extracted or derived from public sources. When a value is estimated or not clearly evidenced, it is marked in the signal. Every signal links to the original source so you can check details before calling. NeoSearch delivers signals for early orientation, not binding project data.",
            link: { href: "/data-security/" },
          },
        ],
      },
      demo: {
        heading: "1:1 with Marko, our Principal Architect.",
        body: "Tell us what you build, where, and from which project size. Then we look into your region free of charge: what was decided recently, and what has not gone to tender yet? We talk about your goals: which projects you want to win, and where NeoSearch gives you the head start.",
        signatureName: "Marko Lazic",
        signatureRole: "Principal Architect, NeoSearch",
        signatureLinkLabel: "Marko Lazic on LinkedIn",
        cta: "Book a demo",
      },
      footerCta: {
        heading: "Your next contract is being decided in a town hall right now.",
        cta: "Find upcoming construction projects",
        ctaNote: "Start free. No credit card required.",
      },
      weekCards: {
        eyebrow: "Recent decisions, across Germany",
        sub: "Real decisions from original municipal documents — with budget, source and the responsible decision-makers.",
        cta: "See more project opportunities →",
        whatDecided: "What was decided",
        dossier: {
          projectLabel: "Project dossier",
          decidersLabel: "People — responsible decision-makers",
          reveal: "Reveal contact",
          namePlaceholder: "Firstname Lastname",
          stages: ["Budget", "Committee", "Decision", "Tender expected"],
        },
      },
      audience: {
        eyebrow: "Who NeoSearch is for",
        heading: "Your next contract is being decided in a town hall right now.",
        sub: "Tell NeoSearch what you build, plan or supply — in your own words.",
        items: [
          {
            title: "Architecture & engineering firms",
            line: "Spot public clients and competitions before the process starts.",
            tags: ["School construction", "Daycare", "Hospitals", "Sports halls"],
          },
          {
            title: "Specialist planners & MEP",
            line: "Find projects before the design phase — while requirements are still open.",
            tags: ["Renovation", "Fire protection", "Energy retrofit"],
          },
          {
            title: "Contractors & general contractors",
            line: "Fill the pipeline before procurement even starts.",
            tags: ["Housing", "Commercial construction", "Civil engineering"],
          },
          {
            title: "Developers & investors",
            line: "See sites and districts before the market knows them.",
            tags: ["District development", "Zoning plans", "Land development", "Contaminated sites"],
          },
          {
            title: "Manufacturers & suppliers",
            line: "Find buildings that need your product — before the tender.",
            tags: ["Car parks", "Flat roofs", "PV & energy", "Façades"],
          },
          {
            title: "Consultants & municipal strategists",
            line: "Track municipal budgets and strategies systematically.",
            tags: ["Municipal budgets", "Digitalization", "Public space"],
          },
        ],
      },
      coverage: {
        stats: [
          { num: "250+", label: "Municipalities" },
          { num: "16", label: "Federal states" },
          { num: "1", label: "Inbox" },
        ],
        cities: [
          "Berlin",
          "Munich",
          "Hamburg",
          "Cologne",
          "Frankfurt",
          "Leipzig",
          "Nuremberg",
          "Bremen",
          "Dresden",
          "Hanover",
        ],
        caption: "One source for every municipal construction decision in Germany.",
      },
      benefits: {
        heading: "Everything you need to act earlier.",
        items: [
          {
            title: "Early project signals",
            desc: "Indications of construction projects before official tenders start.",
          },
          {
            title: "No manual monitoring",
            desc: "Automatic monitoring of municipal sources across Germany.",
          },
          {
            title: "One dashboard",
            desc: "A central overview of cities, projects and development activity.",
          },
          {
            title: "AI summaries",
            desc: "Municipal documents made readable — without hours of reading.",
          },
        ],
      },
      beforeAfter: {
        heading: "Earlier than any tender portal.",
        portalLabel: "Standard tender portals",
        neoLabel: "NeoSearch · municipal decision intelligence",
        badge: "2–6 months earlier",
        rows: [
          {
            without: "Only show a project once it's out to tender.",
            with: "Show the council decision as soon as it's made.",
          },
          {
            without: "Specification and scope are already fixed.",
            with: "With budget, source and the responsible department.",
          },
          {
            without: "You start in open competition — like everyone else.",
            with: "You shape the requirements before it's tendered.",
          },
        ],
      },
    },
    vorsprungDemo: {
      ctaPrimary: "Book a demo",
      ctaPrimaryShort: "Demo",
      ctaNote: "30 minutes, no commitment — with real projects from your region.",
      pricingNote: "We'll show you which plan fits during the demo.",
      inlineLink: "See it live in the demo →",
      heroSub:
        "NeoSearch monitors municipal sources and flags matching construction projects for your region — months before they appear on tender portals.",
      timelineHeadLead: "Discover construction projects",
      timelineHeadAccent: "sooner.",
      timelineLead:
        "In 30 minutes we'll show you live which projects in your region have already been decided or planned — before they appear on tender portals.",
      timelineSteps: [
        "Set project types and search terms",
        "See matches with source, budget and context",
        "Set up automatic search",
      ],
    },
    vorsprungForm: {
      ctaPrimary: "Send a quick inquiry",
      ctaPrimaryShort: "Inquiry",
      ctaNote: "No call needed — we'll follow up with matching signals.",
    },
    demoBooking: {
      title: "Your competitors start with the tender. You start now.",
      lead: "I'll show you live what's already been decided in your region — with budgets and the people behind them.",
      points: [
        "You tell me what you build, plan or supply",
        "I show you real projects from your region — with budget, source and decision-makers",
        "You see where you're still early enough",
      ],
      scrollCta: "Choose a time",
      schedulerHeading: "Available times",
      loading: "Loading available times…",
      signatureName: "Marko Lazic",
      signatureRole: "Principal Architect, NeoSearch",
      signatureLinkLabel: "Marko Lazic on LinkedIn",
    },
    mipim: {
      badge: "NeoSearch · Early Signals",
      headline: "Get into pole position for construction projects in Germany.",
      subheadline:
        "NeoSearch detects early signals from German municipalities so you can identify construction opportunities before your competitors do.",
      supportingLine:
        "Built for investors, developers, suppliers, and city leaders who want to see German projects earlier.",
      primaryCta: "Try Now for Free",
      secondaryCta: "Book a Meeting to Find Out More",
      statsItems: [
        {
          value: "Weeks earlier",
          desc: "Spot projects before they appear on tender platforms",
        },
        {
          value: "All of Germany",
          desc: "Municipal signals across every German state",
        },
        {
          value: "4 audiences, 1 tool",
          desc: "One platform for the full construction value chain",
        },
      ],
      whatHeading: "What NeoSearch does",
      whatPara:
        "NeoSearch monitors municipal planning records, committee agendas, and zoning updates across Germany. When a construction project is in the making, NeoSearch surfaces it — long before it becomes a public tender.",
      whatSources: [
        "Council meeting agendas and minutes",
        "Land-use and zoning change filings",
        "Building committee decisions",
        "Development and infrastructure announcements",
      ],
      audienceHeading: "Who NeoSearch is built for",
      audiences: [
        {
          role: "Investors",
          headline: "Qualify markets before capital is deployed.",
          body: "Surface pipeline activity and development signals across German cities — before assets hit the open market.",
        },
        {
          role: "Developers & owners",
          headline: "Act on municipal decisions before competitors do.",
          body: "Track zoning changes, planning approvals, and nearby development that affects your projects.",
        },
        {
          role: "Suppliers & contractors",
          headline: "Win relationships before RFPs are published.",
          body: "Identify upcoming projects while they are still in the planning phase — and reach decision-makers first.",
        },
        {
          role: "Cities & regions",
          headline: "Understand what your peers are building.",
          body: "Benchmark development activity, track infrastructure trends, and make data-informed investment decisions.",
        },
      ],
      valueHeading: "Your advantage with NeoSearch",
      valueItems: [
        "Get into pole position for new construction projects",
        "Identify opportunities earlier than competitors",
        "Track municipal development across all of Germany",
        "Focus your sales on projects before tenders are published",
      ],
      howHeading: "How NeoSearch works for you",
      howSteps: [
        {
          num: "01",
          title: "Monitor municipalities",
          desc: "NeoSearch continuously scans public data from German municipalities.",
        },
        {
          num: "02",
          title: "Detect early signals",
          desc: "Planning documents, zoning updates, and committee decisions reveal upcoming projects.",
        },
        {
          num: "03",
          title: "Gain pole position",
          desc: "Act before competitors and build relationships before projects enter the tender phase.",
        },
      ],
      howReassurance:
        "NeoSearch is based exclusively on transparent, publicly available information from official municipal sources.",
      examplesHeading: "Examples of real signals",
      examples: [
        {
          title: "Logistics cluster near a major harbour",
          body: "Pre-application consultation surfaced from an economic development briefing. Tender target before year-end.",
          tag: "Industrial",
        },
        {
          title: "60-unit affordable housing redevelopment",
          body: "Preliminary land-use study from a district council meeting. Infrastructure RFP expected in Q3.",
          tag: "Housing",
        },
        {
          title: "Integrated kindergarten and family centre",
          body: "Draft zoning change from a city development committee. Architectural brief expected within weeks.",
          tag: "Education",
        },
      ],
      closingHeadline:
        "NeoSearch puts you in pole position for construction opportunities in Germany.",
      closingSubline: "Start free. No credit card required.",
      closingCta: "Open NeoSearch →",
      closingFootnote: "neoSearch is a product of neoBIM GmbH",
    },
    footer: {
      productDesc: "Early construction signal intelligence for Germany",
      productLine: "NeoSearch is a product of",
      productHeading: "Product",
      legalHeading: "Legal",
      contact: "Contact",
      dataSecurity: "Data Security & Privacy",
      imprint: "Imprint",
      features: "Features",
      pricing: "Pricing",
      how: "How it Works",
      insights: "Insights",
      blog: "Blog",
      copyright: "© 2026 NeoSearch. All rights reserved.",
      complianceNote:
        "NeoSearch processes only publicly available data and user-provided information in accordance with EU and German data protection regulations.",
    },
    cookieConsent: {
      bannerText: "We use cookies to improve your experience.",
      settingsLabel: "Cookie settings",
      learnMoreLabel: "Learn more",
      acceptAllLabel: "Accept all",
      acceptNecessaryLabel: "Necessary only",
      modalTitle: "Cookie Preferences",
      necessaryTitle: "Necessary Cookies",
      necessaryDescription:
        "Required for the basic functioning of the website and cannot be disabled.",
      analyticsTitle: "Analytics Cookies",
      analyticsDescription:
        "Help us understand how visitors use our website — fully anonymised.",
      marketingTitle: "Marketing Cookies",
      marketingDescription:
        "Used to track visitors across websites for marketing and advertising purposes.",
      cancelLabel: "Cancel",
      saveLabel: "Save preferences",
    },
    proof: {
      clientsLabel: "Trusted by leading companies",
      pressLabel: "Featured in",
    },
    anfrage: {
      eyebrow: "Quick Inquiry",
      heading: "Tell us what you're looking for.",
      sub: "No call needed — a short form, and we'll follow up with matching signals.",
      nameLabel: "Name",
      emailLabel: "Work Email",
      companyLabel: "Company",
      intentLabel: "What are you building, planning, or delivering?",
      intentPlaceholder: "e.g. \"School construction, southern Germany\" or \"TU/GC tenders nationwide\"",
      regionsLabel: "Which region are you looking for projects in?",
      regionPlaceholder: "Select a region",
      wholeGermany: "Whole Germany",
      states: [
        "Baden-Württemberg",
        "Bavaria",
        "Berlin",
        "Brandenburg",
        "Bremen",
        "Hamburg",
        "Hesse",
        "Mecklenburg-Western Pomerania",
        "Lower Saxony",
        "North Rhine-Westphalia",
        "Rhineland-Palatinate",
        "Saarland",
        "Saxony",
        "Saxony-Anhalt",
        "Schleswig-Holstein",
        "Thuringia",
      ],
      messageLabel: "Anything else?",
      messagePlaceholder: "Optional — anything else that would help us.",
      optionalTag: "optional",
      submitCta: "Send inquiry",
      submitting: "Sending …",
      requiredNote: "* Required field",
      requiredError: "Required",
      emailInvalidError: "Invalid email address",
      successTitle: "Thanks, that came through.",
      successBody: "We'll get back to you personally, shortly.",
      successDemoPrompt: "Don't want to wait?",
      successDemoCta: "Book a demo now",
      errorTitle: "That didn't go through.",
      errorBody:
        "Your inquiry could not be sent. Please try again or reach out to us directly.",
      retryCta: "Try again",
    },
  },
};
